import React, { useCallback, useEffect, useState } from 'react';
import Cropper, { Area } from 'react-easy-crop';
import { Button, ModalPortal } from './ui';
import { getCroppedAvatarFile } from '../utils/cropImage';

interface PhotoCropModalProps {
  imageSrc: string;
  fileName?: string;
  isOpen: boolean;
  onCancel: () => void;
  onComplete: (file: File, previewUrl: string) => void;
  title?: string;
}

export const PhotoCropModal: React.FC<PhotoCropModalProps> = ({
  imageSrc,
  fileName = 'avatar.jpg',
  isOpen,
  onCancel,
  onComplete,
  title = 'Adjust photo',
}) => {
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen) return;
    setCrop({ x: 0, y: 0 });
    setZoom(1);
    setCroppedAreaPixels(null);
    setError(null);
    setSaving(false);
  }, [isOpen, imageSrc]);

  const onCropComplete = useCallback((_area: Area, pixels: Area) => {
    setCroppedAreaPixels(pixels);
  }, []);

  const handleApply = async () => {
    if (!croppedAreaPixels) return;
    setSaving(true);
    setError(null);
    try {
      const { file, previewUrl } = await getCroppedAvatarFile(
        imageSrc,
        croppedAreaPixels,
        fileName,
      );
      onComplete(file, previewUrl);
    } catch {
      setError('Could not crop this image. Try another photo.');
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <ModalPortal>
    <div className="fixed inset-0 z-[80] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onCancel} />

      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden">
        <div className="flex items-center justify-between px-5 pt-5 pb-3 border-b border-gray-100">
          <div>
            <h2 className="text-base font-bold text-gray-900">{title}</h2>
            <p className="text-xs text-gray-400 mt-0.5">Drag to reposition · pinch or zoom to frame</p>
          </div>
          <button
            type="button"
            onClick={onCancel}
            className="w-8 h-8 rounded-lg flex items-center justify-center text-gray-400 hover:bg-gray-100 hover:text-gray-600 transition-colors"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="relative h-72 bg-gray-900">
          <Cropper
            image={imageSrc}
            crop={crop}
            zoom={zoom}
            aspect={1}
            cropShape="round"
            showGrid={false}
            onCropChange={setCrop}
            onZoomChange={setZoom}
            onCropComplete={onCropComplete}
          />
        </div>

        <div className="px-5 py-4 space-y-4">
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <label htmlFor="photo-zoom" className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                Zoom
              </label>
              <span className="text-xs text-gray-400">{Math.round(zoom * 100)}%</span>
            </div>
            <input
              id="photo-zoom"
              type="range"
              min={1}
              max={3}
              step={0.05}
              value={zoom}
              onChange={e => setZoom(Number(e.target.value))}
              className="w-full accent-brand-600"
            />
          </div>

          {error && <p className="text-sm text-red-600">{error}</p>}

          <div className="flex gap-2">
            <Button type="button" variant="secondary" onClick={onCancel} fullWidth>
              Cancel
            </Button>
            <Button type="button" onClick={handleApply} loading={saving} fullWidth>
              Use this photo
            </Button>
          </div>
        </div>
      </div>
    </div>
    </ModalPortal>
  );
};
